// src/data/immigrationGuides/britishPassport.js

const britishPassport = {
    slug: "british-passport-application",

    title: "British Passport Application After Citizenship Guide",

    // Matches the pattern used by the other guides. Without this, GuidePage
    // emits no BreadcrumbList JSON-LD for the page. "/immigration-guides" is
    // deliberately not used as the parent: it is not a real route, and pointing
    // a BreadcrumbList item at a 404 emits invalid structured data.
    breadcrumbs: [
        {
            name: "Home",
            url: "/"
        },
        {
            name: "Study Guide",
            url: "/study-guide"
        },
        {
            name: "British Passport Application",
            url: "/british-passport-application"
        }
    ],

    seo: {
        metaTitle:
            "British Passport Application: First Passport Guide",
        metaDescription:
            "How to apply for your first British passport after citizenship: fees, countersignatory rules, documents needed, processing times, and refusal reasons.",
        keywords: [
            "British passport after citizenship",
            "first UK passport",
            "British passport application",
            "British passport eligibility"
        ]
    },

    introduction: `
Once you've completed naturalisation or registration and received your certificate
of British citizenship, you can [apply for your first British passport](https://www.gov.uk/apply-first-adult-passport).
This is a separate process from the citizenship application itself, handled by
His Majesty's Passport Office (HMPO), not the Home Office.

A first adult passport application is treated differently from a renewal: because
you have no previous UK passport on record, HMPO needs to independently verify your
identity. That normally means providing your certificate of naturalisation or
registration and getting an adult who has known you personally for at least two
years to countersign your form and photo.

Fees and processing times below are correct as verified against GOV.UK, but both
change periodically. Always confirm the current figures on GOV.UK immediately
before you apply.
`,

    sections: [
        {
            heading: "Who can apply for a first British passport",
            content: `
You can apply for a first adult British passport if you:

- are a British citizen (or hold another form of British nationality, see below)
- are 16 or over, or will turn 16 within 3 weeks of applying
- have never held a UK passport before

You must also use the "first adult passport" application route if your last UK
passport was issued before 1 January 1994, even if you've held a British passport
previously.

If you naturalised or registered as a British citizen, you need to have received
your certificate of naturalisation or registration from the Home Office first,
because you cannot apply for a passport with only proof that your ceremony is
booked or that your application was approved. The certificate itself, or the letter HMPO
accepts in place of it while the certificate is delayed, is the core evidence of
your citizenship for the passport application.

Source: [GOV.UK - Apply for your first adult passport](https://www.gov.uk/apply-first-adult-passport)
`
        },

        {
            heading: "Current passport fees (verify before applying)",
            content: `
As verified on GOV.UK, the standard 34-page adult passport costs:

- **£102** if you apply online
- **£115.50** if you apply using a paper form

Applying online is £13.50 cheaper than applying by post. There's also a 54-page
"frequent traveller" passport, priced at £116 online / £129.50 by paper, aimed at
people who travel very frequently and fill pages quickly. Most first-time
applicants don't need it.

Child passport fees are lower (£66.50 online / £80 paper for a standard 34-page
child passport) but don't apply to a first adult passport application.

Passport fees are reviewed and changed by HMPO periodically. Treat the figures
above as a strong estimate rather than gospel, and confirm the exact current fee
on the GOV.UK fees page before you pay.

Source: [GOV.UK - Passport fees](https://www.gov.uk/passport-fees)
`
        },

        {
            heading: "Processing times and urgent applications",
            content: `
GOV.UK does not commit to a fixed number of weeks for a standard first adult
passport application on its overview pages: it directs applicants to check
current processing timescales on the HMPO services page immediately before
applying, because timescales move with application volumes. As a first-time
applicant, build in a buffer beyond whatever the standard estimate says: identity
verification (including checking your countersignatory's declaration and your
naturalisation documents) can take longer for first applications than for simple
renewals, where HMPO already holds your data on file.

**Do not book travel until you have a valid passport in hand.** Your new passport
will have a different number from any travel document you've used before, so
airlines, visa applications, and travel insurance tied to an old document number
won't carry over.

If you have a genuinely urgent need to travel (for example, a family emergency or
medical treatment abroad), GOV.UK points applicants to two expedited options:

- **Online Premium service**: a same-day appointment-based service at a passport
  office, for people who need to travel very soon
- **1 week Fast Track service**: a faster turnaround than the standard service

Both carry a higher fee than the standard application, and eligibility/current
pricing should be checked directly on GOV.UK, since access to these services can
be limited or paused during high-demand periods. For medical or family emergencies,
contact the Passport Adviceline rather than relying on the standard online form.

Source: [GOV.UK - Apply for your first adult passport](https://www.gov.uk/apply-first-adult-passport)
`
        },

        {
            heading: "Documents you need for a first adult passport",
            content: `
For a first application after naturalisation or registration, you'll typically need:

- your original certificate of naturalisation or registration (not a photocopy)
- evidence of your identity, such as a driving licence or other photo ID, if
  requested
- your birth certificate, if you have one and HMPO asks for it
- if you hold a non-British passport, that document's name and gender must
  match what you want on your British passport, or you'll need to update the
  non-British document first
- a completed application form (online or paper) and payment

Original documents sent by post are normally returned once your application is
processed, but check current guidance on how HMPO wants documents submitted for
your chosen application route (online applications typically involve uploading
scans/photos of documents rather than posting originals).

Source: [GOV.UK - Apply for your first adult passport](https://www.gov.uk/apply-first-adult-passport)
`
        },

        {
            heading: "The countersignatory requirement",
            content: `
Because you have no passport history for HMPO to check you against, a first adult
passport application (almost) always needs a countersignatory: someone who
confirms your identity is genuinely who you say you are.

Your countersignatory must:

- have known you personally for at least 2 years
- hold a current British or Irish passport
- not be related to you by blood or marriage, in a relationship with you, or
  living at your address
- be "a person of good standing in their community," or work in (or be retired
  from) a recognised profession (GOV.UK gives examples of qualifying occupations
  on its countersigning page)

Your countersignatory needs to write a declaration on the back of one of your
photos, sign and date it, and provide their own passport number on your
application form. Choose someone early in the process, since finding a suitable
countersignatory at the last minute is a common cause of delay for first-time
applicants.

Source: [GOV.UK - Countersigning passport applications and photos](https://www.gov.uk/countersigning-passport-applications)
`
        },

        {
            heading: "Photo requirements",
            content: `
Whether you apply online or by paper form, your photo must meet HMPO's standard
passport photo rules: a plain cream or light grey background, a neutral expression
with your mouth closed, no shadows or glare, no head covering (unless worn daily
for religious or medical reasons, in which case your face must still be fully
visible from chin to forehead), and no other people or objects in the shot.

Online applications generally use a digital photo (a good smartphone photo often
works, or you can use a photo booth or professional photographer and upload the
image), while paper applications need a printed photo signed by your
countersignatory. Passport photo booths and many pharmacies/photo shops in the UK
advertise "passport-compliant" digital codes or prints specifically for this
purpose. Checking the GOV.UK photo checker before submitting avoids a rejected
application and a re-do.

Source: [GOV.UK - Apply for your first adult passport](https://www.gov.uk/apply-first-adult-passport)
`
        },

        {
            heading: "Keeping your citizenship certificate safe",
            content: `
Your certificate of naturalisation or registration is one of the most important
documents you'll hold as a new British citizen. Keep it safe:

- You'll need it not just for this passport application but potentially for
  other future evidence-of-status purposes
- Keep the original somewhere secure and take clear copies (front and back)
  before you send it anywhere
- If it's lost, replacing it involves a separate Home Office process, distinct
  from your passport application, and can add significant delay before you can
  even start your first passport application
`
        },

        {
            heading: "When a passport can be refused or withheld",
            content: `
Having British nationality does not guarantee you a passport. Passports remain
the property of the Crown and can be refused, cancelled, or withdrawn at any time.

You may not get a new passport (or your existing passport may be taken from you) if:

- you're suspected of a serious crime and an arrest warrant has been issued
- a court order stops you having a UK passport or restricts your travel
- you're on bail and your bail conditions mean you cannot leave the UK
- you've previously been brought back to the UK at the government's expense and
  have not repaid what you owe
- you've received a European Union or United Nations order restricting your travel

A child's passport can also be cancelled or not renewed if there's a court order
in place stopping the child from leaving the UK.

Your eligibility and entitlement to a British passport is considered every time
you apply, not just the first time.

Source: [GOV.UK - British passport eligibility](https://www.gov.uk/british-passport-eligibility)
`
        },

        {
            heading: "Which types of British nationality can get a passport",
            content: `
You can apply for a British passport if you hold one of the following:

- British citizen
- British overseas territories citizen
- British overseas citizen
- British subject
- British national (overseas)
- British protected person

Most people who naturalise or register as adults under the standard routes covered
elsewhere on this site become British citizens, which carries the broadest set of
passport and travel rights of these categories.

Source: [GOV.UK - British passport eligibility](https://www.gov.uk/british-passport-eligibility)
`
        }
    ],

    checklist: [
        "Receive your certificate of naturalisation or registration",
        "Choose and brief your countersignatory (known you 2+ years, holds British/Irish passport)",
        "Take a compliant digital or printed passport photo",
        "Gather supporting ID documents (and any non-British passport, if held)",
        "Check the current fee and processing time on GOV.UK",
        "Complete the application online or by paper form",
        "Have your countersignatory sign your photo and provide their passport number",
        "Submit the application and pay",
        "Avoid booking travel until the new passport arrives"
    ],

    faqs: [
        {
            question: "Can I apply for a passport immediately after citizenship?",
            answer:
                "Yes, but only once you've actually received your certificate of naturalisation or registration from the Home Office. An approval letter or a booked citizenship ceremony date isn't enough on its own. Once you hold the certificate, you can start your first adult passport application straight away."
        },
        {
            question: "How much does a first British passport cost?",
            answer:
                "As verified on GOV.UK, a standard adult passport costs £102 online or £115.50 by paper form. Fees change periodically, so confirm the exact current figure on the GOV.UK passport fees page before you pay, rather than relying on any figure quoted here or elsewhere."
        },
        {
            question: "How long does a first adult passport application take?",
            answer:
                "GOV.UK does not publish a fixed guarantee in weeks and instead asks applicants to check current processing timescales immediately before applying, since they move with demand. First-time applications can take longer than renewals because HMPO has no existing record to verify you against, so build in extra buffer time and avoid booking travel until your passport arrives."
        },
        {
            question: "Do I need someone to countersign my first passport application?",
            answer:
                "Almost always, yes. Your countersignatory must have known you personally for at least 2 years, hold a current British or Irish passport, not be related to you or living at your address, and be a person of good standing in their community or in a recognised profession. They sign a declaration on the back of one of your photos and provide their own passport number on your form."
        },
        {
            question: "Can I get my first passport faster if I need to travel urgently?",
            answer:
                "GOV.UK lists an Online Premium service and a 1 week Fast Track service for urgent cases, both at a higher fee than the standard application. Availability and current pricing should be checked directly on GOV.UK, and for medical or family emergencies you can also contact the Passport Adviceline."
        },
        {
            question: "Can a British passport be refused even if I have British nationality?",
            answer:
                "Yes. Having British nationality does not guarantee a passport: it can be refused or withheld, for example if you're suspected of a serious crime, subject to a court travel restriction, on bail with travel conditions, or have unpaid repatriation costs to the government. Eligibility is reassessed every time you apply."
        },
        {
            question: "Which types of British nationality can get a British passport?",
            answer:
                "British citizens, British overseas territories citizens, British overseas citizens, British subjects, British nationals (overseas), and British protected persons can all apply for a British passport, though British citizenship carries the broadest travel rights."
        }
    ],

    relatedPages: [
        {
            title: "Citizenship Ceremony",
            url: "/citizenship-ceremony"
        },
        {
            title: "British Citizenship Guide",
            url: "/british-citizenship-guide"
        }
    ],

    officialSources: [
        {
            title: "GOV.UK - Apply for First Adult Passport",
            url: "https://www.gov.uk/apply-first-adult-passport"
        },
        {
            title: "GOV.UK - Passport Fees",
            url: "https://www.gov.uk/passport-fees"
        },
        {
            title: "GOV.UK - Countersigning Passport Applications and Photos",
            url: "https://www.gov.uk/countersigning-passport-applications"
        },
        {
            title: "GOV.UK - British Passport Eligibility",
            url: "https://www.gov.uk/british-passport-eligibility"
        }
    ],

    metadata: {
        contentType: "guide",
        category: "Immigration Guides",
        author: "Life in UK Coach",
        lastUpdated: "2026-07-31"
    }
};

export default britishPassport;
